import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShapesIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/90c0599f-244f-459f-a268-ba1295b0fa15/1792294331531_musik-klar-mockup-v2-volymer-korrekta-e61ee6a7.webp';
export const heroGuarantees = ['Omedelbar digital nedladdning', 'Engångsbetalning', 'Ingen prenumeration', '7 dagars nöjdhetsgaranti'];
export const stats = [{
  value: '240',
  label: 'Sidor i huvudvolymerna'
}, {
  value: '6',
  label: 'Digitala musikvolymer'
}, {
  value: '9',
  label: 'Årskurser – från 1 till 9'
}, {
  value: '5',
  label: 'Bonusar som ingår'
}, {
  value: '1',
  label: 'Komplett undervisningssystem'
}];
export const differenceStats = [{
  value: 'Årskurs 1–9',
  label: 'Material organiserat i tre stadier'
}, {
  value: '6 digitala volymer',
  label: 'Från puls och rytm till musikproduktion och musikanalys'
}, {
  value: 'Utskrivbara PDF-filer',
  label: 'Att använda digitalt eller skriva ut i musiksalen'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Söndagskvällarna försvinner',
  text: 'Timmar framför datorn för att planera nästa veckas musiklektioner – och du är fortfarande inte klar.'
}, {
  title: 'Timmar av letande',
  text: 'Aktiviteter och låtar från olika webbplatser är för lätta, för svåra eller måste anpassas innan de går att använda.'
}, {
  title: 'Arbetsbladen skapas från grunden',
  text: 'Noter, rytmrutor, lyssningsfrågor och facit – det som skulle ta minuter blir en lång arbetsuppgift.'
}, {
  title: 'Svårt att göra musiken visuell',
  text: 'Rytmkort, notbilder, instrumentbilder och lyssningskartor tar tid att ta fram för varje nytt moment.'
}, {
  title: 'Bedömning och uppföljning tar tid',
  text: 'Spelprov, lyssningsuppgifter och uppföljning av elevernas utveckling blir ännu mer arbete efter skoltid.'
}, {
  title: 'Stress inför nästa lektion',
  text: 'När tiden inte räcker till blir det lätt att improvisera och få ihop material i sista minuten.'
}];
export const afterItems = ['Ett organiserat musikpaket för hela grundskolan', 'Lektionsupplägg som är enkla att använda', 'Färdiga musikaktiviteter', 'Arbetsblad som kan skrivas ut direkt', 'Visuella rytmövningar och rytmkort', 'Aktiviteter för sång, lyssning och spel', 'Musikspel som skapar variation', 'Bedömningsmaterial och facit', 'Planeringsstöd för lektionerna'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Söndagarna går åt till planering',
  text: 'Lägger du delar av helgen på att skapa musiklektioner inför kommande vecka?'
}, {
  icon: FolderSearchIcon,
  title: 'Du vet inte vilken aktivitet du ska använda',
  text: 'Letar du ständigt efter övningar och låtar som passar elevernas nivå?'
}, {
  icon: LightbulbIcon,
  title: 'Du vill ha tydligare förklaringar',
  text: 'Behöver du mer visuellt material för att förklara rytm, noter och musikbegrepp på ett enkelt sätt?'
}, {
  icon: PencilRulerIcon,
  title: 'Du vill slippa skapa allt från noll',
  text: 'Önskar du att lektioner, uppgifter och aktiviteter redan var förberedda?'
}, {
  icon: ShuffleIcon,
  title: 'Eleverna behöver mer variation',
  text: 'Vill du använda fler spel, rytmlekar och praktiska aktiviteter i undervisningen?'
}, {
  icon: LayoutGridIcon,
  title: 'Du behöver en tydligare struktur',
  text: 'Vill du ha materialet samlat så att du snabbt hittar det du behöver?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volym 01',
  level: 'Åk 1–3',
  title: 'Puls, rytm och rörelse',
  tagline: 'Spela, klappa och skapa – färdiga musiklektioner för årskurs 1–3.',
  intro: 'Den första volymen innehåller 14 färdiga lektioner på 30 minuter om puls, rytm och rörelse – grunden för allt musicerande.',
  items: ['Puls och rytm', 'Rörelse till musik', 'Kroppsperkussion', 'Härmningsövningar', 'Tempo och dynamik', 'Pauser och tystnad', 'Skapa egna rytmmönster', 'Rytmkort och elevblad', 'Observation och självskattning'],
  outro: 'Varje lektion har mål, material, start, huvudaktivitet, tillämpning och avslutning.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 02',
  level: 'Åk 1–3',
  title: 'Sång, ljud och musikskapande',
  tagline: 'Låt eleverna sjunga, utforska ljud och skapa egen musik.',
  intro: 'Den andra volymen fokuserar på sång, ljudutforskande, instrument och små egna skapelser.',
  items: ['Sång och röst', 'Utforska ljud', 'Instrument', 'Musikaliska berättelser', 'Små egna kompositioner', 'Utskrivbara elevblad', 'Anpassningar för olika nivåer'],
  outro: 'Färdiga aktiviteter där eleverna får sjunga, lyssna, utforska och skapa.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 03',
  level: 'Åk 4–6',
  title: 'Spela tillsammans',
  tagline: 'Få hela klassen att spela tillsammans.',
  intro: 'Den tredje volymen handlar om samspel i grupp med rytmmönster, melodier och enkla komp.',
  items: ['Samspel i grupp', 'Rytmmönster', 'Melodier', 'Enkla komp', 'Instruktioner steg för steg', 'Utskrivbara elevblad', 'Stöd för bedömning'],
  outro: 'Ett praktiskt stöd för att organisera gemensamt musicerande i klassrummet.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 04',
  level: 'Åk 4–6',
  title: 'Skapa och förstå musik',
  tagline: 'Hjälp eleverna att skapa och förstå musik.',
  intro: 'Den fjärde volymen kombinerar komposition, musikaliska symboler, guidat lyssnande och projekt med digitala verktyg.',
  items: ['Komposition', 'Musikaliska symboler', 'Guidat lyssnande', 'Projekt med digitala verktyg', 'Visuella uppgifter', 'Utskrivbara elevblad', 'Stöd för bedömning'],
  outro: 'Tydliga uppgifter som gör musikskapandet konkret.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 05',
  level: 'Åk 7–9',
  title: 'Ensemblespel och låtskrivande',
  tagline: 'Låt eleverna arrangera, skriva och framföra egen musik.',
  intro: 'Den femte volymen innehåller ensemblespel, arrangemang, låtskrivande, repetitioner och redovisning av projekt.',
  items: ['Ensemblespel', 'Arrangemang', 'Låtskrivande', 'Repetitioner', 'Redovisning av projekt', 'Utskrivbara elevblad', 'Stöd för bedömning'],
  outro: 'Strukturerat material för musikundervisningen på högstadiet.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 06',
  level: 'Åk 7–9',
  title: 'Musikproduktion och musikanalys',
  tagline: 'Fördjupa elevernas förståelse för musik och musikproduktion.',
  intro: 'Den sjätte volymen innehåller digital musikproduktion, genreanalys, musik och samhälle samt slutprojekt.',
  items: ['Digital musikproduktion', 'Analys av genrer', 'Musik och samhälle', 'Slutprojekt', 'Utskrivbara elevblad', 'Stöd för bedömning'],
  outro: 'Ett komplett avslutande steg för fördjupning och analys.',
  note: 'Övningarna är självständigt framtagna och är inte officiellt material från Skolverket.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitala volymer för årskurs 1–9',
  text: 'Ett organiserat system med färdiga musiklektioner, aktiviteter och elevblad för lågstadiet, mellanstadiet och högstadiet. Öppna volymen, välj aktivitet och skriv ut. Mindre planering. Mer musik.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Färdiga lektioner',
  text: 'Slipp börja från ett tomt dokument. Tydliga lektionsupplägg, exempel och aktiviteter hjälper dig att komma igång snabbare.'
}, {
  icon: FileTextIcon,
  title: 'Utskrivbara arbetsblad',
  text: 'Musikuppgifter som kan skrivas ut och användas direkt – för självständigt arbete, repetition och extra träning.'
}, {
  icon: ShapesIcon,
  title: 'Visuella musikövningar',
  text: 'Rytmkort, symboler, instrumentbilder och tydliga diagram gör musiken mer konkret och lättare att förstå.'
}, {
  icon: DicesIcon,
  title: 'Musikspel och aktiviteter',
  text: 'Spel, rytmlekar och praktiska övningar ger eleverna fler sätt att träna och använda sina kunskaper.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Bedömningar och facit',
  text: 'Färdiga bedömningsuppgifter och lösningar som stöd för rättning och uppföljning.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplanering',
  text: 'Stöd för att organisera musikundervisningen och välja arbetsområden utifrån din planering.'
}];
export const pageSamples: Array<{src: string; label: string; title: string; text: string}> = [
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/90c0599f-244f-459f-a268-ba1295b0fa15/1792285892243_musik-klar-v1-sida-6-folj-pulssparet-4770d1e8.webp', label: 'Arbetsblad · Sida 6', title: 'Följ pulsspåret', text: 'Ett utskrivbart arbetsblad där eleverna följer pulsen från start till slut – med händer, fötter och klapp.' },
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/90c0599f-244f-459f-a268-ba1295b0fa15/1792285895966_musik-klar-v1-sida-5-hitta-pulsen-792b1b10.webp', label: 'Praktisk lektion · Sida 5', title: 'Lektion 1: Hitta pulsen', text: 'En färdig lektion minut för minut med mål, start, huvudaktivitet, tillämpning och avslutning.' },
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/90c0599f-244f-459f-a268-ba1295b0fa15/1792285899828_musik-klar-v1-sida-3-fyra-nycklar-96fff60e.webp', label: 'Visuell lektion · Sida 3', title: 'Fyra nycklar till musiken', text: 'Puls, rytm, tempo och dynamik förklarade visuellt – så att eleverna förstår musikens grunder direkt.' },
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/90c0599f-244f-459f-a268-ba1295b0fa15/1792285903623_musik-klar-v1-sida-33-musiklekar-885f66c2.webp', label: 'Musiklekar · Sida 33', title: 'Sex snabba musiklekar', text: 'Färdiga lekar på 3–5 minuter som skapar energi, fokus och variation i musiksalen.' },
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/90c0599f-244f-459f-a268-ba1295b0fa15/1792285906921_musik-klar-v1-sida-36-det-har-kan-jag-98d38644.webp', label: 'Formativ bedömning · Sida 36', title: 'Det här kan jag!', text: 'Elevens självskattning med tydliga mål – ett enkelt underlag för uppföljning och bedömning.' }
];
export const pageTypes = ['Färdiga lektioner', 'Utskrivbara arbetsblad', 'Praktiska aktiviteter', 'Bedömningar och facit'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Musikstart på 5 minuter',
  text: '30 snabba startaktiviteter för musikundervisningen i årskurs 1–9.',
  items: ['Puls och rytmiskt eko', 'Rörelse, tempo och dynamik', 'Röst och lyssning', 'Minne, skapande och grupparbete'],
  benefit: 'Med aktivitetsväljare och en plan för sex veckor.'
}, {
  title: 'Vikariepaketet – musik',
  text: '6 färdiga musiklektioner på 40 minuter för vikarier – två för åk 1–3, två för åk 4–6 och två för åk 7–9.',
  items: ['Lärarguide och elevblad', 'Exempel och facit', 'Observationsstöd', 'Överlämningsmall till ordinarie lärare'],
  benefit: 'Fungerar utan internet, utan instrument och utan att någon behöver sjunga solo eller läsa noter.'
}, {
  title: 'Musik utan instrument',
  text: 'Skapa musik med kroppen, rösten och vardagliga föremål.',
  items: ['Ljudbibliotek med kropp, röst, papper och kartong', 'Kort och ljudexperiment', 'Visuella partitur och tidslinjer', 'Grupprojekt som Vardagsorkestern'],
  benefit: 'Mer musicerande i klassrummet – även när instrumenten inte räcker till.'
}, {
  title: 'Musikbingo och instrumentkort',
  text: 'Utskrivbara spel för att upptäcka instrument och musikbegrepp.',
  items: ['12 instrumentkort och 12 ledtrådskort', '6 bingobrickor med instrument', '2 bingobrickor och 9 kort med musikbegrepp', 'Extra spel och facit'],
  benefit: 'Fungerar med bilder och ledtrådar – inga ljudfiler behövs.'
}, {
  title: 'Konsertpaketet',
  text: 'Planera, repetera och genomför en musikalisk klasskonsert på 15–25 minuter med material ni redan har arbetat med.',
  items: ['Plan för fyra veckor och snabbt alternativ', 'Rollkort och repetitionsplan', 'Programblad och inbjudan', 'Checklistor och reservplaner'],
  benefit: 'Kräver ingen scen eller professionell utrustning.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Öppna MUSIK KLAR™',
  text: 'Ladda ner paketet och öppna volymen som passar din årskurs.'
}, {
  title: 'Välj lektion och material',
  text: 'Hitta rätt arbetsområde och välj de övningar, arbetsblad eller aktiviteter som passar dina elever.'
}, {
  title: 'Förbered dig på 5 minuter',
  text: 'Gå igenom instruktionerna och välj det material du behöver.'
}, {
  title: 'Skriv ut och undervisa',
  text: 'Skriv ut arbetsbladen eller använd materialet digitalt – och gå in i musiksalen med en tydlig plan.'
}];
export const audienceItems = ['Undervisar i musik i årskurs 1–3, 4–6 eller 7–9', 'Vill ha musikmaterialet samlat i ett organiserat system', 'Vill slippa skapa varje arbetsblad från grunden', 'Behöver fler visuella rytmövningar och elevblad', 'Vill använda musikspel och praktiska aktiviteter', 'Vill ha enkel tillgång till bedömningsmaterial och facit', 'Vill spara tid på veckoplaneringen', 'Vill känna sig bättre förberedd inför undervisningen'];
export const timeValueTasks = ['Leta efter aktiviteter och låtar', 'Skriva övningar', 'Skapa visuella förklaringar', 'Förbereda aktiviteter', 'Bedöma och följa upp elevernas arbete'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitala volymer – 240 sidor',
  detail: 'Puls, rytm, sång, samspel, skapande, ensemble, låtskrivande och musikproduktion'
}, {
  label: '5 bonusar utan extra kostnad',
  detail: 'Musikstart, vikariepaket, musik utan instrument, musikbingo och konsertpaket'
}, {
  label: 'För årskurs 1–9',
  detail: 'Färdiga lektioner, arbetsblad och facit'
}];
export const offerIncludes = ['Omedelbar digital nedladdning', 'PDF-filer att spara', 'Utskrivbara arbetsblad', 'Ingen fysisk produkt skickas'];
export const PRICE = '299 kr';

// Only add authentic reviews of MUSIK KLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hur får jag materialet?',
  a: 'MUSIK KLAR™ levereras digitalt. Efter köpet får du tillgång till PDF-filer som du kan ladda ner och spara. Du kan använda materialet på dator, surfplatta eller mobil och skriva ut de arbetsblad du behöver.'
}, {
  q: 'Är MUSIK KLAR™ anpassat för den svenska skolan?',
  a: 'Materialet är framtaget för musikundervisning i den svenska grundskolan, årskurs 1–9. Det är ett fristående undervisningsmaterial och inte en officiell produkt från Skolverket.'
}, {
  q: 'Passar det för årskurs 1–9?',
  a: 'Ja. De sex volymerna är organiserade i tre stadier – årskurs 1–3, 4–6 och 7–9. Du väljer material utifrån den årskurs och det musikaliska område du undervisar i.'
}, {
  q: 'Kan jag verkligen förbereda en lektion på 5 minuter?',
  a: 'MUSIK KLAR™ är utformat för att göra lektionsförberedelserna snabbare. Du kan öppna en volym, välja ett färdigt arbetsblad eller lektionsupplägg och gå igenom det på ungefär fem minuter. Tiden kan variera beroende på lektionsinnehåll, elevgrupp och behov av anpassning.'
}, {
  q: 'Hur använder jag materialet under läsåret?',
  a: 'MUSIK KLAR™ är organiserat i sex volymer för tre stadier. Du väljer, kombinerar och anpassar lektionerna och aktiviteterna efter din årsplanering och elevernas behov.'
}, {
  q: 'Finns det färdiga arbetsblad?',
  a: 'Ja. Materialet innehåller utskrivbara arbetsblad med uppgifter, övningar och visuella förklaringar.'
}, {
  q: 'Ingår facit?',
  a: 'Ja. Paketet innehåller facit och lösningar som stöd för rättning och genomgång.'
}, {
  q: 'Kan jag använda materialet för elever på olika nivåer?',
  a: 'Ja. MUSIK KLAR™ innehåller varierade uppgifter och aktiviteter som du kan välja och anpassa utifrån elevernas kunskaper och behov.'
}, {
  q: 'Måste jag skriva ut allt?',
  a: 'Nej. Du väljer själv vilka sidor du skriver ut, och du kan även använda delar av materialet digitalt.'
}, {
  q: 'Ingår de fem bonusarna?',
  a: `Ja. Alla fem bonusar ingår i paketet utan extra kostnad – allt för ${PRICE}.`
}, {
  q: 'Är det en prenumeration?',
  a: 'Nej. Du betalar en gång och får tillgång till det digitala materialet. Inga återkommande avgifter.'
}, {
  q: 'Kan jag spara filerna?',
  a: 'Ja. Du kan ladda ner och spara PDF-filerna för fortsatt användning enligt produktens användarvillkor.'
}, {
  q: 'Kan jag använda materialet på mobil eller surfplatta?',
  a: 'Ja. PDF-filerna kan öppnas på dator, surfplatta och mobil. För att granska layouten på arbetsbladen före utskrift rekommenderar vi dator eller surfplatta.'
}, {
  q: 'Finns det någon garanti?',
  a: 'Ja. MUSIK KLAR™ har 7 dagars nöjdhetsgaranti. Om materialet inte motsvarar dina förväntningar kan du kontakta oss och begära återbetalning inom garantiperioden enligt villkoren.'
}];
export const footerLinks = ['Köpvillkor', 'Integritetspolicy', 'Återbetalningspolicy', 'Kontakt'];
